/* *
 * =============================================================================
 * AscendDraft Coupon Code System
 * =============================================================================
 *
 * Integrates with the chat app's Hybrid AI Coupon System for validation.
 * Coupons are managed in the chat app admin panel, NOT on the website.
 *
 * FEATURES:
 * - Validate coupon codes via chat app API
 * - Apply percentage or fixed discounts
 * - Support referral, merit, promotional coupons
 * - Admin can enable/disable coupon feature
 * - Visual feedback for coupon status
 *
 * API Endpoints (Chat App):
 * - POST /api/coupons/validate - Validate a coupon code
 * - POST /api/coupons/calculate - Calculate price with coupon
 * - GET /api/coupons/my - Get user's coupons
 *
 * @version 2.0.0 */
"use strict";

// ==========================================================================
// CONFIGURATION
// ==========================================================================

const CONFIG = {
  apiBase: window.ASCEND_CHAT_API || "https://api.ascenddraft.com/api",
  enabled: true, // Can be disabled by admin
  storageKey: "ascend_applied_coupon",
  cacheKey: "ascend_coupon_cache",
  cacheTTL: 5 * 60 * 1000, // 5 minutes
};

// ==========================================================================
// STATE
// ==========================================================================

let appliedCoupon = null;
let couponDiscount = 0;
let couponsEnabled = true;
let validationCache = new Map();

// ==========================================================================
// INITIALIZATION
// ==========================================================================

function init() {
  // Check if coupons are enabled by admin
  checkCouponStatus();

  // Load any previously applied coupon
  loadAppliedCoupon();

  // Inject coupon UI after basket renders.
  // The basket fires 'ascend:basketReady' once its DOM is in place; we also
  // retry at 800 ms and 2 s to handle pages where the event fires before us.
  function tryInject() {
    if (couponsEnabled) injectCouponUI();
  }

  window.addEventListener("ascend:basketReady", tryInject, { once: true });
  setTimeout(tryInject, 800);
  setTimeout(tryInject, 2000);

  console.log("[CouponSystem] Initialized, enabled:", couponsEnabled);
}

function checkCouponStatus() {
  // Check AdminPricing for coupon enable/disable status
  if (window.AdminPricing) {
    const config = window.AdminPricing.getConfig?.();
    if (config && config.couponsDisabled === true) {
      couponsEnabled = false;
    }
  }

  // Also check localStorage setting
  const adminSetting = localStorage.getItem("ascend_coupons_enabled");
  if (adminSetting === "false") {
    couponsEnabled = false;
  }
}

function loadAppliedCoupon() {
  try {
    const stored = sessionStorage.getItem(CONFIG.storageKey);
    if (stored) {
      appliedCoupon = JSON.parse(stored);
      // Sanitize legacy format where discount was stored as an object
      if (appliedCoupon && typeof appliedCoupon.discount !== "number") {
        const raw = appliedCoupon.discount;
        appliedCoupon.discount =
          typeof raw === "object" && raw !== null
            ? (raw.value ?? raw.amount ?? raw.percentage ?? 0)
            : parseFloat(raw) || 0;
        // Re-save sanitized version
        sessionStorage.setItem(
          CONFIG.storageKey,
          JSON.stringify(appliedCoupon),
        );
      }
      console.log("[CouponSystem] Restored coupon:", appliedCoupon?.code);
    }
  } catch (e) {
    sessionStorage.removeItem(CONFIG.storageKey);
  }
}

// ==========================================================================
// API VALIDATION (Chat App Integration)
// ==========================================================================

/* *
 * Validate coupon code via chat app API */
async function validateCouponAPI(code, orderDetails = {}) {
  const couponCode = code.toUpperCase().trim();

  // Check cache first
  const cacheKey = `${couponCode}_${JSON.stringify(orderDetails)}`;
  const cached = validationCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CONFIG.cacheTTL) {
    return cached.result;
  }

  try {
    const token = localStorage.getItem("ascend_access_token");
    const response = await fetch(`${CONFIG.apiBase}/coupons/validate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        code: couponCode,
        subtotal: orderDetails.subtotal,
        services: orderDetails.services || [],
      }),
    });

    if (!response.ok) {
      // On auth errors (401) or server errors, fall back to local validation
      // rather than showing "Invalid coupon code" to the user.
      console.warn(
        "[CouponSystem] API returned",
        response.status,
        "— falling back to local validation",
      );
      return validateCouponLocal(code, orderDetails);
    }

    const data = await response.json();

    if (data.success && data.data) {
      // Server returns: { valid, coupon: { discountType, discountValue, maxDiscount }, discount: { amount, type } }
      const serverData = data.data;
      const couponObj = serverData.coupon || {};
      const discountType =
        couponObj.discountType || serverData.discount?.type || "percentage";
      const discountValue =
        couponObj.discountValue ?? serverData.discount?.amount ?? 0;
      const maxDiscount =
        couponObj.maxDiscount ?? serverData.discount?.maxDiscount ?? null;

      const result = {
        valid: true,
        coupon: {
          code: couponCode,
          discount: discountValue,
          type: (() => {
            if (discountType === "percentage" || discountType === "percent")
              return "percent";
            if (
              discountType === "fixed_amount" ||
              discountType === "fixed" ||
              discountType === "subtraction"
            )
              return "fixed";
            if (discountType === "addition") return "addition";
            if (discountType === "division") return "division";
            return "percent";
          })(),
          maxDiscount,
        },
      };

      // Cache result
      validationCache.set(cacheKey, { result, timestamp: Date.now() });

      return result;
    } else {
      return {
        valid: false,
        error: data.data?.error || data.error || "Coupon validation failed",
      };
    }
  } catch (error) {
    console.warn(
      "[CouponSystem] API validation failed, using fallback:",
      error.message,
    );
    // Fallback to local validation if API unavailable
    return validateCouponLocal(code, orderDetails);
  }
}

/* *
 * Local/fallback validation when API is unavailable */
function validateCouponLocal(code, orderDetails = {}) {
  const couponCode = code.toUpperCase().trim();

  // Check AdminPricing for locally created coupons
  if (window.AdminPricing) {
    return window.AdminPricing.validateCoupon(code, orderDetails);
  }

  // Minimal default coupons
  const defaultCoupons = {
    WELCOME5: { discount: 5, type: "percent", enabled: true },
    FIRSTORDER: { discount: 5, type: "percent", enabled: true },
  };

  const coupon = defaultCoupons[couponCode];

  if (!coupon) {
    return { valid: false, error: "Invalid coupon code" };
  }

  if (!coupon.enabled) {
    return { valid: false, error: "Coupon is no longer active" };
  }

  return {
    valid: true,
    coupon: { ...coupon, code: couponCode },
  };
}

/* *
 * Main validation function - tries API first, then fallback */
async function validateCoupon(code, orderDetails = {}) {
  if (!couponsEnabled) {
    return { valid: false, error: "Coupons are currently disabled" };
  }

  // Try API first
  const result = await validateCouponAPI(code, orderDetails);
  return result;
}

// ==========================================================================
// APPLY/REMOVE COUPON
// ==========================================================================

async function applyCoupon(code, subtotal) {
  if (!couponsEnabled) {
    return { success: false, error: "Coupons are currently disabled" };
  }

  const validation = await validateCoupon(code, { subtotal });

  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  appliedCoupon = validation.coupon;

  // Calculate discount
  const cv = validation.coupon;
  if (cv.type === "percent") {
    couponDiscount = (subtotal * cv.discount) / 100;
    if (cv.maxDiscount)
      couponDiscount = Math.min(couponDiscount, cv.maxDiscount);
  } else if (cv.type === "division") {
    // divide the subtotal — e.g. value=2 means half price → discount = subtotal/2
    couponDiscount = cv.discount > 1 ? subtotal - subtotal / cv.discount : 0;
  } else if (cv.type === "addition") {
    // treat as flat addition to the discount (same maths as fixed)
    couponDiscount = Math.min(cv.discount, subtotal);
  } else {
    // 'fixed' / 'subtraction' — flat amount off, capped at subtotal
    couponDiscount = Math.min(cv.discount, subtotal);
  }

  // Save to session
  sessionStorage.setItem(CONFIG.storageKey, JSON.stringify(appliedCoupon));

  // Update UI
  updateCouponUI(true);

  // Emit event
  document.dispatchEvent(
    new CustomEvent("couponApplied", {
      detail: {
        code: appliedCoupon.code,
        discount: couponDiscount,
        percent: validation.coupon.discount,
        type: validation.coupon.type,
      },
    }),
  );

  return {
    success: true,
    code: appliedCoupon.code,
    discount: couponDiscount,
    percent: validation.coupon.discount,
  };
}

function removeCoupon() {
  const removedCode = appliedCoupon?.code;

  appliedCoupon = null;
  couponDiscount = 0;

  sessionStorage.removeItem(CONFIG.storageKey);

  updateCouponUI(false);

  document.dispatchEvent(
    new CustomEvent("couponRemoved", {
      detail: { code: removedCode },
    }),
  );

  return true;
}

function getAppliedCoupon() {
  return appliedCoupon;
}

function getCouponDiscount() {
  return couponDiscount;
}

function recalculateDiscount(newSubtotal) {
  if (!appliedCoupon) return 0;

  if (appliedCoupon.type === "percent") {
    couponDiscount = (newSubtotal * appliedCoupon.discount) / 100;
    if (appliedCoupon.maxDiscount) {
      couponDiscount = Math.min(couponDiscount, appliedCoupon.maxDiscount);
    }
  } else if (appliedCoupon.type === "division") {
    couponDiscount =
      appliedCoupon.discount > 1
        ? newSubtotal - newSubtotal / appliedCoupon.discount
        : 0;
  } else {
    // 'fixed', 'subtraction', 'addition' — flat amount, capped at subtotal
    couponDiscount = Math.min(appliedCoupon.discount, newSubtotal);
  }

  return couponDiscount;
}

// ==========================================================================
// ADMIN CONTROLS
// ==========================================================================

function enableCoupons() {
  couponsEnabled = true;
  localStorage.setItem("ascend_coupons_enabled", "true");
  injectCouponUI();
  console.log("[CouponSystem] Coupons enabled");
}

function disableCoupons() {
  couponsEnabled = false;
  localStorage.setItem("ascend_coupons_enabled", "false");
  removeCoupon();
  hideCouponUI();
  console.log("[CouponSystem] Coupons disabled");
}

function isCouponsEnabled() {
  return couponsEnabled;
}

// ==========================================================================
// UI MANAGEMENT
// ==========================================================================

function injectCouponUI() {
  if (!couponsEnabled) return;

  // Bundle checkouts: price is fixed. Coupon field lives on the bundle card.
  // If ?bundle=1 is set but user DID enter a code (?coupon=X), still inject so
  // the code can be pre-filled + auto-applied.
  const _urlParams = new URLSearchParams(window.location.search);
  const _isBundle = _urlParams.get("bundle") === "1";
  const _urlCoupon = _urlParams.get("coupon") || "";
  if (_isBundle && !_urlCoupon) return; // bundle with no code → hide coupon UI

  const containers = [
    document.querySelector(".basket-summary"),
    document.querySelector(".order-summary"),
    document.querySelector(".checkout-summary"),
    document.querySelector(".unified-basket-footer"),
    document.querySelector("#basket-summary"),
  ].filter(Boolean);

  containers.forEach((container) => {
    if (container.querySelector(".coupon-section")) return;

    const couponSection = document.createElement("div");
    couponSection.className = "coupon-section";
    couponSection.innerHTML = getCouponHTML();

    const totalElement = container.querySelector(
      ".basket-total, .order-total, .checkout-total",
    );
    if (totalElement) {
      totalElement.parentNode.insertBefore(couponSection, totalElement);
    } else {
      container.appendChild(couponSection);
    }

    setupCouponEvents(couponSection);

    // If the user entered a code on the bundle card, pre-fill + auto-apply
    if (_urlCoupon) {
      const _input = couponSection.querySelector(
        "input[type='text'], .coupon-input, .ub-coupon-input",
      );
      if (_input) {
        _input.value = _urlCoupon;
        // Auto-apply after a short tick so the event handlers are wired
        setTimeout(() => {
          const _applyBtn = couponSection.querySelector(
            "button, .coupon-btn, .ub-coupon-btn",
          );
          if (_applyBtn) _applyBtn.click();
        }, 300);
      }
    }
  });
}

/* *
 * Inject coupon UI into a specific container element
 * @param {HTMLElement} container - The container to inject into */
function injectUI(container) {
  console.log(
    "[CouponSystem] injectUI called, enabled:",
    couponsEnabled,
    "container:",
    !!container,
  );

  if (!container) {
    console.warn("[CouponSystem] No container provided");
    return;
  }

  // Always show coupon UI unless explicitly disabled
  if (!couponsEnabled) {
    console.log("[CouponSystem] Coupons disabled, hiding UI");
    container.style.display = "none";
    return;
  }

  // Clear existing content
  container.innerHTML = "";

  // Add dark theme styles
  const darkStyles = `
      <style>
        .ub-coupon-box {
          padding: 12px;
          border-top: 1px solid rgba(255,255,255,0.1);
          margin-top: 8px;
        }
        .ub-coupon-label {
          font-size: 12px;
          color: rgba(255,255,255,0.7);
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .ub-coupon-input-row {
          display: flex;
          gap: 8px;
        }
        .ub-coupon-input {
          flex: 1;
          padding: 8px 12px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          color: #fff;
          font-size: 13px;
        }
        .ub-coupon-input:focus {
          outline: none;
          border-color: var(--accent-gold, #d4af37);
        }
        .ub-coupon-input::placeholder {
          color: rgba(255,255,255,0.4);
        }
        .ub-coupon-btn {
          padding: 8px 16px;
          background: var(--accent-gold, #d4af37);
          color: #0a1628;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ub-coupon-btn:hover {
          background: #e5c349;
          transform: translateY(-1px);
        }
        .ub-coupon-btn.loading {
          opacity: 0.6;
          pointer-events: none;
        }
        .ub-coupon-error {
          font-size: 11px;
          color: #ef4444;
          margin-top: 6px;
          display: none;
        }
        .ub-coupon-applied {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 6px;
        }
        .ub-coupon-applied-info {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .ub-coupon-applied-info .code {
          font-weight: 600;
          color: var(--accent-emerald, #10b981);
        }
        .ub-coupon-applied-info .discount {
          font-size: 12px;
          color: rgba(255,255,255,0.7);
        }
        .ub-coupon-remove {
          background: none;
          border: none;
          color: #ef4444;
          cursor: pointer;
          font-size: 12px;
          padding: 4px 8px;
        }
        .ub-coupon-remove:hover {
          text-decoration: underline;
        }
        .ub-coupon-discount-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: var(--accent-emerald, #10b981);
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed rgba(255,255,255,0.1);
        }
      </style>
    `;

  const isApplied = !!appliedCoupon;

  // Ensure discount is a safe number for display
  const _dispDisc =
    typeof appliedCoupon?.discount === "number" &&
    !isNaN(appliedCoupon.discount)
      ? appliedCoupon.discount
      : typeof appliedCoupon?.discount === "object" && appliedCoupon.discount
        ? (appliedCoupon.discount.value ??
          appliedCoupon.discount.amount ??
          appliedCoupon.discount.percentage ??
          0)
        : 0;
  const _safeCouponDiscount = isNaN(couponDiscount) ? 0 : couponDiscount;
  const _discLabel =
    appliedCoupon?.type === "percent" ? `${_dispDisc}%` : `${_dispDisc} off`;

  const html = isApplied
    ? `
      ${darkStyles}
      <div class="ub-coupon-box">
        <div class="ub-coupon-applied">
          <div class="ub-coupon-applied-info">
            <span class="code">🏷️ ${appliedCoupon.code}</span>
            <span class="discount">-${_discLabel}</span>
          </div>
          <button class="ub-coupon-remove" data-action="remove-coupon">• Remove</button>
        </div>
        <div class="ub-coupon-discount-row">
          <span>Coupon Discount</span>
          <span>-${_safeCouponDiscount.toFixed(2)}</span>
        </div>
      </div>
    `
    : `
      ${darkStyles}
      <div class="ub-coupon-box">
        <div class="ub-coupon-label">🏷️ Have a coupon code?</div>
        <div class="ub-coupon-input-row">
          <input type="text" class="ub-coupon-input" placeholder="Enter code" maxlength="20">
          <button class="ub-coupon-btn" data-action="apply-coupon">Apply</button>
        </div>
        <div class="ub-coupon-error"></div>
      </div>
    `;

  container.innerHTML = html;

  // Setup events
  const applyBtn = container.querySelector('[data-action="apply-coupon"]');
  const removeBtn = container.querySelector('[data-action="remove-coupon"]');
  const input = container.querySelector(".ub-coupon-input");
  const errorDiv = container.querySelector(".ub-coupon-error");

  if (applyBtn && input) {
    applyBtn.addEventListener("click", async () => {
      const code = input.value.trim();
      if (!code) {
        if (errorDiv) {
          errorDiv.textContent = "Please enter a coupon code";
          errorDiv.style.display = "block";
        }
        return;
      }

      applyBtn.classList.add("loading");
      applyBtn.textContent = "Checking...";

      const subtotal = getCurrentSubtotal();
      const result = await applyCoupon(code, subtotal);

      applyBtn.classList.remove("loading");
      applyBtn.textContent = "Apply";

      if (result.success) {
        // Re-inject UI to show applied state
        injectUI(container);
        // Trigger basket update to reflect new total
        if (window.ServiceBasket) {
          window.ServiceBasket.renderBasketIndicator?.();
        }
      } else {
        if (errorDiv) {
          errorDiv.textContent = result.error;
          errorDiv.style.display = "block";
        }
      }
    });

    input.addEventListener("keypress", (e) => {
      if (e.key === "Enter") applyBtn.click();
    });

    input.addEventListener("input", () => {
      if (errorDiv) errorDiv.style.display = "none";
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener("click", () => {
      removeCoupon();
      injectUI(container);
      if (window.ServiceBasket) {
        window.ServiceBasket.notifyChange?.();
      }
    });
  }
}

function hideCouponUI() {
  const sections = document.querySelectorAll(".coupon-section");
  sections.forEach((section) => section.remove());
}

function getCouponHTML() {
  const isApplied = !!appliedCoupon;

  return `
      <style>
        .coupon-section {
          margin: 16px 0;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px dashed #d1d5db;
        }
        .coupon-input-group {
          display: flex;
          gap: 8px;
        }
        .coupon-input {
          flex: 1;
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          text-transform: uppercase;
        }
        .coupon-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .coupon-btn {
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .coupon-btn:hover {
          background: #2563eb;
        }
        .coupon-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
        .coupon-btn.loading {
          opacity: 0.7;
          pointer-events: none;
        }
        .coupon-applied {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          background: #d1fae5;
          border-radius: 6px;
          color: #065f46;
        }
        .coupon-applied-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .coupon-remove {
          background: none;
          border: none;
          color: #991b1b;
          cursor: pointer;
          font-size: 12px;
        }
        .coupon-error {
          color: #dc2626;
          font-size: 12px;
          margin-top: 8px;
        }
        .coupon-discount-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          color: #10b981;
          font-weight: 500;
        }
      </style>
      
      <div class="coupon-container">
        <div class="coupon-label" style="font-size: 13px; font-weight: 500; margin-bottom: 8px; color: #374151;">
          🏷️ Have a coupon code?
        </div>
        
        ${
          isApplied
            ? `
          <div class="coupon-applied">
            <div class="coupon-applied-info">
              <span style="font-weight: 600;">${appliedCoupon.code}</span>
              <span>-${appliedCoupon.discount}${appliedCoupon.type === "percent" ? "%" : " USD"}</span>
            </div>
            <button class="coupon-remove" data-action="remove">• Remove</button>
          </div>
          <div class="coupon-discount-row">
            <span>Coupon Discount</span>
            <span>-$${couponDiscount.toFixed(2)}</span>
          </div>
        `
            : `
          <div class="coupon-input-group">
            <input type="text" class="coupon-input" placeholder="Enter coupon code" maxlength="20">
            <button class="coupon-btn" data-action="apply">Apply</button>
          </div>
          <div class="coupon-error" style="display: none;"></div>
        `
        }
      </div>
    `;
}

function setupCouponEvents(container) {
  const applyBtn = container.querySelector('[data-action="apply"]');
  const removeBtn = container.querySelector('[data-action="remove"]');
  const input = container.querySelector(".coupon-input");
  const errorDiv = container.querySelector(".coupon-error");

  if (applyBtn && input) {
    applyBtn.addEventListener("click", async () => {
      const code = input.value.trim();
      if (!code) {
        showError(errorDiv, "Please enter a coupon code");
        return;
      }

      applyBtn.classList.add("loading");
      applyBtn.textContent = "Validating...";

      const subtotal = getCurrentSubtotal();
      const result = await applyCoupon(code, subtotal);

      applyBtn.classList.remove("loading");
      applyBtn.textContent = "Apply";

      if (result.success) {
        refreshCouponUI(container);
      } else {
        showError(errorDiv, result.error);
      }
    });

    input.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        applyBtn.click();
      }
    });

    input.addEventListener("input", () => {
      if (errorDiv) errorDiv.style.display = "none";
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener("click", () => {
      removeCoupon();
      refreshCouponUI(container);
    });
  }
}

function showError(errorDiv, message) {
  if (errorDiv) {
    errorDiv.textContent = message;
    errorDiv.style.display = "block";
  }
}

function refreshCouponUI(container) {
  container.innerHTML = getCouponHTML();
  setupCouponEvents(container);

  if (window.UnifiedBasket) {
    window.UnifiedBasket.renderBasketIndicator();
  }
}

function updateCouponUI(applied) {
  const sections = document.querySelectorAll(".coupon-section");
  sections.forEach((section) => {
    section.innerHTML = getCouponHTML();
    setupCouponEvents(section);
  });
}

// ==========================================================================
// HELPERS
// ==========================================================================

function getCurrentSubtotal() {
  if (window.UnifiedBasket) {
    try {
      const items = window.UnifiedBasket.getAllItems?.() || [];
      return items.reduce((sum, item) => {
        const qty = item.quantity || 1;
        const price = parseFloat(item.price) || 0;
        return sum + price * qty;
      }, 0);
    } catch (e) {
      console.warn("[CouponSystem] Could not get basket subtotal:", e);
    }
  }

  const subtotalEl = document.querySelector(
    ".basket-subtotal, .order-subtotal, [data-subtotal]",
  );
  if (subtotalEl) {
    const text = subtotalEl.textContent || subtotalEl.dataset.subtotal;
    return parseFloat(text.replace(/[^0-9.]/g, "")) || 0;
  }

  return 0;
}

// ==========================================================================
// CHECKOUT INTEGRATION
// ==========================================================================

function getCheckoutData() {
  return {
    hasCoupon: !!appliedCoupon,
    couponCode: appliedCoupon?.code || null,
    couponDiscount: couponDiscount,
    couponPercent: appliedCoupon?.discount || 0,
    couponType: appliedCoupon?.type || "percent",
  };
}

function applyToOrder(order) {
  if (!appliedCoupon) return order;

  const updatedOrder = { ...order };

  updatedOrder.coupon = {
    code: appliedCoupon.code,
    discount: couponDiscount,
    percent: appliedCoupon.discount,
    type: appliedCoupon.type,
  };

  if (typeof updatedOrder.total === "number") {
    updatedOrder.total = Math.max(0, updatedOrder.total - couponDiscount);
  }

  return updatedOrder;
}

function renderCouponInput(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;
  if (!couponsEnabled) {
    container.innerHTML = "";
    return;
  }

  container.innerHTML = getCouponHTML();
  setupCouponEvents(container);
}

// ==========================================================================
// PUBLIC API
// ==========================================================================

const AscendCoupons = {
  init,
  validateCoupon,
  applyCoupon,
  removeCoupon,
  getAppliedCoupon,
  getCouponDiscount,
  recalculateDiscount,
  getCheckoutData,
  applyToOrder,
  renderCouponInput,
  injectCouponUI,
  injectUI,
  // Admin controls
  enableCoupons,
  disableCoupons,
  isCouponsEnabled,
  // Config
  CONFIG,
};

// Auto-init
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// Export for ES modules
export default AscendCoupons;

// Also export individual functions
export {
  init,
  validateCoupon,
  applyCoupon,
  removeCoupon,
  getAppliedCoupon,
  getCouponDiscount,
  recalculateDiscount,
  getCheckoutData,
  applyToOrder,
  renderCouponInput,
  injectCouponUI,
  enableCoupons,
  disableCoupons,
  isCouponsEnabled,
};

// Also keep global for backward compatibility (in non-module scripts)
if (typeof window !== "undefined") {
  window.CouponSystem = AscendCoupons;
  window.AscendCoupons = AscendCoupons;
}
